/**
 * SignalR Service
 * Handles real-time communication with the API using SignalR
 * Enhanced with connection resilience for mobile/desktop scenarios
 */

import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { getSignalRFullUrl } from '@/config/environment'

class SignalRService {
  constructor() {
    this.connection = null
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.reconnectDelay = 1000
    this.maxReconnectDelay = 30000
    this.connectionHealth = {
      isConnected: false,
      lastPing: null,
      lastPong: null,
      latency: null,
      connectionQuality: 'unknown'
    }
    this.resilience = {
      pageVisible: true,
      networkOnline: true,
      windowFocused: true,
      userActive: true,
      lastActivity: Date.now(),
      autoReconnect: true
    }
    this.eventHandlers = new Map()
    this.globalEventHandlers = new Map()
    this.connectionStatus = 'disconnected'
    this.connectionStatusDetails = ''
    
    // Initialize resilience features
    this.initializeResilience()
    
    // Start connection health monitoring
    this.startHealthMonitoring()
  }

  // Initialize resilience features
  initializeResilience() {
    // Page Visibility API
    document.addEventListener('visibilitychange', () => {
      this.resilience.pageVisible = !document.hidden
      this.handleVisibilityChange()
    })

    // Network online/offline detection
    window.addEventListener('online', () => {
      this.resilience.networkOnline = true
      this.handleNetworkChange(true)
    })

    window.addEventListener('offline', () => {
      this.resilience.networkOnline = false
      this.handleNetworkChange(false)
    })

    // Window focus/blur events
    window.addEventListener('focus', () => {
      this.resilience.windowFocused = true
      this.handleFocusChange(true)
    })

    window.addEventListener('blur', () => {
      this.resilience.windowFocused = false
      this.handleFocusChange(false)
    })

    // User activity tracking
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.resilience.userActive = true
        this.resilience.lastActivity = Date.now()
      }, { passive: true })
    })

    // Periodic activity check
    setInterval(() => {
      const timeSinceActivity = Date.now() - this.resilience.lastActivity
      this.resilience.userActive = timeSinceActivity < 300000 // 5 minutes
    }, 60000) // Check every minute
  }

  // Handle page visibility changes
  handleVisibilityChange() {
    if (this.resilience.pageVisible && this.connectionStatus === 'disconnected') {
      console.log('Page became visible, attempting to reconnect...')
      this.connect()
    } else if (!this.resilience.pageVisible) {
      console.log('Page hidden, connection will be maintained')
    }
  }

  // Handle network changes
  handleNetworkChange(isOnline) {
    if (isOnline && this.connectionStatus === 'disconnected') {
      console.log('Network back online, attempting to reconnect...')
      this.connect()
    } else if (!isOnline) {
      console.log('Network offline, connection may be lost')
      this.updateConnectionStatus('network_offline', 'Network connection lost')
    }
  }

  // Handle window focus changes
  handleFocusChange(isFocused) {
    if (isFocused && this.connectionStatus === 'disconnected') {
      console.log('Window focused, attempting to reconnect...')
      this.connect()
    }
  }

  // Start connection health monitoring
  startHealthMonitoring() {
    setInterval(() => {
      this.checkConnectionHealth()
    }, 30000) // Check every 30 seconds
  }

  // Check connection health
  async checkConnectionHealth() {
    if (!this.connection || this.connection.state !== 'Connected') {
      return
    }

    try {
      const startTime = Date.now()
      await this.connection.invoke('Ping')
      const latency = Date.now() - startTime
      
      this.connectionHealth.lastPing = new Date()
      this.connectionHealth.latency = latency
      
      // Determine connection quality based on latency
      if (latency < 100) {
        this.connectionHealth.connectionQuality = 'excellent'
      } else if (latency < 300) {
        this.connectionHealth.connectionQuality = 'good'
      } else if (latency < 1000) {
        this.connectionHealth.connectionQuality = 'fair'
      } else {
        this.connectionHealth.connectionQuality = 'poor'
      }
    } catch (error) {
      console.warn('Connection health check failed:', error)
      this.connectionHealth.connectionQuality = 'poor'
    }
  }

  // Connect to SignalR hub
  async connect() {
    if (this.isConnecting || this.connection?.state === 'Connected') {
      return
    }

    this.isConnecting = true
    this.updateConnectionStatus('connecting', 'Connecting to server...')

    try {
      // Get authentication token
      const token = this.getAuthToken()
      if (!token) {
        throw new Error('No authentication token available')
      }

      // Build connection
      const hubUrl = getSignalRFullUrl()
      console.log('🔗 SignalR connecting to:', hubUrl)
      
      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: 1 // WebSockets only
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount === 0) {
              return 0
            }
            
            if (retryContext.previousRetryCount < 3) {
              return 1000
            }
            
            if (retryContext.previousRetryCount < 5) {
              return 5000
            }
            
            return 10000
          }
        })
        .configureLogging(LogLevel.Information)
        .build()

      // Set up connection event handlers
      this.setupConnectionHandlers()

      // Set up all module event handlers
      this.setupModuleEventHandlers()

      // Start connection
      await this.connection.start()
      
      this.isConnecting = false
      this.reconnectAttempts = 0
      this.updateConnectionStatus('connected', 'Connected to server')
      
      console.log('SignalR connected successfully')
      
      // Send initial connection info
      await this.connection.invoke('RegisterUser', this.getCurrentUserId())

    } catch (error) {
      this.isConnecting = false
      this.handleConnectionError(error)
    }
  }

  // Set up connection event handlers
  setupConnectionHandlers() {
    this.connection.onclose((error) => {
      console.log('SignalR connection closed:', error)
      this.updateConnectionStatus('disconnected', 'Connection lost')
      this.handleDisconnection(error)
    })

    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting:', error)
      this.updateConnectionStatus('reconnecting', 'Reconnecting...')
    })

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected:', connectionId)
      this.updateConnectionStatus('connected', 'Reconnected to server')
      this.reconnectAttempts = 0
    })

    // Handle pong response
    this.connection.on('Pong', (timestamp) => {
      this.connectionHealth.lastPong = new Date()
      const latency = Date.now() - timestamp
      this.connectionHealth.latency = latency
    })
  }

  // Set up all module event handlers
  setupModuleEventHandlers() {
    // ===== NOTIFICATION EVENTS =====
    this.connection.on('NotificationReceived', (notification) => {
      console.log('Notification received:', notification)
      this.handleNotification(notification)
      this.triggerGlobalEvent('notification', notification)
    })

    this.connection.on('NotificationUpdated', (notification) => {
      console.log('Notification updated:', notification)
      this.triggerGlobalEvent('notificationUpdated', notification)
    })

    this.connection.on('NotificationDeleted', (notificationId) => {
      console.log('Notification deleted:', notificationId)
      this.triggerGlobalEvent('notificationDeleted', notificationId)
    })

    // ===== DASHBOARD STATISTICS EVENTS =====
    this.connection.on('DashboardStatsUpdated', (stats) => {
      console.log('Dashboard stats updated:', stats)
      this.triggerGlobalEvent('dashboardStatsUpdated', stats)
    })

    this.connection.on('EmployeeStatsUpdated', (stats) => {
      console.log('Employee stats updated:', stats)
      this.triggerGlobalEvent('employeeStatsUpdated', stats)
    })

    this.connection.on('AttendanceStatsUpdated', (stats) => {
      console.log('Attendance stats updated:', stats)
      this.triggerGlobalEvent('attendanceStatsUpdated', stats)
    })

    this.connection.on('LeaveStatsUpdated', (stats) => {
      console.log('Leave stats updated:', stats)
      this.triggerGlobalEvent('leaveStatsUpdated', stats)
    })

    this.connection.on('PayrollStatsUpdated', (stats) => {
      console.log('Payroll stats updated:', stats)
      this.triggerGlobalEvent('payrollStatsUpdated', stats)
    })

    this.connection.on('KpiStatsUpdated', (stats) => {
      console.log('KPI stats updated:', stats)
      this.triggerGlobalEvent('kpiStatsUpdated', stats)
    })

    this.connection.on('RecruitmentStatsUpdated', (stats) => {
      console.log('Recruitment stats updated:', stats)
      this.triggerGlobalEvent('recruitmentStatsUpdated', stats)
    })

    // ===== RECRUITMENT EVENTS =====
    this.connection.on('ManpowerRequestCreated', (request) => {
      console.log('Manpower request created:', request)
      this.triggerGlobalEvent('manpowerRequestCreated', request)
    })

    this.connection.on('ManpowerRequestUpdated', (request) => {
      console.log('Manpower request updated:', request)
      this.triggerGlobalEvent('manpowerRequestUpdated', request)
    })

    this.connection.on('ManpowerRequestApproved', (request) => {
      console.log('Manpower request approved:', request)
      this.triggerGlobalEvent('manpowerRequestApproved', request)
    })

    this.connection.on('ManpowerRequestRejected', (request) => {
      console.log('Manpower request rejected:', request)
      this.triggerGlobalEvent('manpowerRequestRejected', request)
    })

    this.connection.on('JobAnnouncementPublished', (announcement) => {
      console.log('Job announcement published:', announcement)
      this.triggerGlobalEvent('jobAnnouncementPublished', announcement)
    })

    this.connection.on('JobApplicationReceived', (application) => {
      console.log('Job application received:', application)
      this.triggerGlobalEvent('jobApplicationReceived', application)
    })

    this.connection.on('JobApplicationStatusChanged', (application) => {
      console.log('Job application status changed:', application)
      this.triggerGlobalEvent('jobApplicationStatusChanged', application)
    })

    this.connection.on('InterviewScheduled', (interview) => {
      console.log('Interview scheduled:', interview)
      this.triggerGlobalEvent('interviewScheduled', interview)
    })

    this.connection.on('InterviewCompleted', (interview) => {
      console.log('Interview completed:', interview)
      this.triggerGlobalEvent('interviewCompleted', interview)
    })

    // ===== LEAVE MANAGEMENT EVENTS =====
    this.connection.on('LeaveRequestSubmitted', (leave) => {
      console.log('Leave request submitted:', leave)
      this.triggerGlobalEvent('leaveRequestSubmitted', leave)
    })

    this.connection.on('LeaveRequestApproved', (leave) => {
      console.log('Leave request approved:', leave)
      this.triggerGlobalEvent('leaveRequestApproved', leave)
    })

    this.connection.on('LeaveRequestRejected', (leave) => {
      console.log('Leave request rejected:', leave)
      this.triggerGlobalEvent('leaveRequestRejected', leave)
    })

    this.connection.on('LeaveRequestCancelled', (leave) => {
      console.log('Leave request cancelled:', leave)
      this.triggerGlobalEvent('leaveRequestCancelled', leave)
    })

    this.connection.on('LeaveBalanceUpdated', (balance) => {
      console.log('Leave balance updated:', balance)
      this.triggerGlobalEvent('leaveBalanceUpdated', balance)
    })

    // ===== ATTENDANCE EVENTS =====
    this.connection.on('CheckInRecorded', (attendance) => {
      console.log('Check-in recorded:', attendance)
      this.triggerGlobalEvent('checkInRecorded', attendance)
    })

    this.connection.on('CheckOutRecorded', (attendance) => {
      console.log('Check-out recorded:', attendance)
      this.triggerGlobalEvent('checkOutRecorded', attendance)
    })

    this.connection.on('AttendanceCalculated', (attendance) => {
      console.log('Attendance calculated:', attendance)
      this.triggerGlobalEvent('attendanceCalculated', attendance)
    })

    this.connection.on('OvertimeCalculated', (overtime) => {
      console.log('Overtime calculated:', overtime)
      this.triggerGlobalEvent('overtimeCalculated', overtime)
    })

    this.connection.on('LateArrivalDetected', (attendance) => {
      console.log('Late arrival detected:', attendance)
      this.triggerGlobalEvent('lateArrivalDetected', attendance)
    })

    this.connection.on('EarlyDepartureDetected', (attendance) => {
      console.log('Early departure detected:', attendance)
      this.triggerGlobalEvent('earlyDepartureDetected', attendance)
    })

    this.connection.on('AbsenceDetected', (absence) => {
      console.log('Absence detected:', absence)
      this.triggerGlobalEvent('absenceDetected', absence)
    })

    // ===== PAYROLL EVENTS =====
    this.connection.on('SalaryCalculated', (salary) => {
      console.log('Salary calculated:', salary)
      this.triggerGlobalEvent('salaryCalculated', salary)
    })

    this.connection.on('PayrollProcessed', (payroll) => {
      console.log('Payroll processed:', payroll)
      this.triggerGlobalEvent('payrollProcessed', payroll)
    })

    this.connection.on('DeductionApplied', (deduction) => {
      console.log('Deduction applied:', deduction)
      this.triggerGlobalEvent('deductionApplied', deduction)
    })

    this.connection.on('BonusAwarded', (bonus) => {
      console.log('Bonus awarded:', bonus)
      this.triggerGlobalEvent('bonusAwarded', bonus)
    })

    this.connection.on('AdvanceRequested', (advance) => {
      console.log('Advance requested:', advance)
      this.triggerGlobalEvent('advanceRequested', advance)
    })

    this.connection.on('AdvanceApproved', (advance) => {
      console.log('Advance approved:', advance)
      this.triggerGlobalEvent('advanceApproved', advance)
    })

    // ===== KPI & PERFORMANCE EVENTS =====
    this.connection.on('KpiEvaluationSubmitted', (evaluation) => {
      console.log('KPI evaluation submitted:', evaluation)
      this.triggerGlobalEvent('kpiEvaluationSubmitted', evaluation)
    })

    this.connection.on('KpiEvaluationApproved', (evaluation) => {
      console.log('KPI evaluation approved:', evaluation)
      this.triggerGlobalEvent('kpiEvaluationApproved', evaluation)
    })

    this.connection.on('PerformanceReviewScheduled', (review) => {
      console.log('Performance review scheduled:', review)
      this.triggerGlobalEvent('performanceReviewScheduled', review)
    })

    this.connection.on('PerformanceReviewCompleted', (review) => {
      console.log('Performance review completed:', review)
      this.triggerGlobalEvent('performanceReviewCompleted', review)
    })

    this.connection.on('IncentiveCalculated', (incentive) => {
      console.log('Incentive calculated:', incentive)
      this.triggerGlobalEvent('incentiveCalculated', incentive)
    })

    // ===== TRAINING EVENTS =====
    this.connection.on('TrainingScheduled', (training) => {
      console.log('Training scheduled:', training)
      this.triggerGlobalEvent('trainingScheduled', training)
    })

    this.connection.on('TrainingCompleted', (training) => {
      console.log('Training completed:', training)
      this.triggerGlobalEvent('trainingCompleted', training)
    })

    this.connection.on('TrainingAttendanceRecorded', (attendance) => {
      console.log('Training attendance recorded:', attendance)
      this.triggerGlobalEvent('trainingAttendanceRecorded', attendance)
    })

    this.connection.on('TrainingFeedbackSubmitted', (feedback) => {
      console.log('Training feedback submitted:', feedback)
      this.triggerGlobalEvent('trainingFeedbackSubmitted', feedback)
    })

    // ===== EMPLOYEE EVENTS =====
    this.connection.on('EmployeeProfileUpdated', (employee) => {
      console.log('Employee profile updated:', employee)
      this.triggerGlobalEvent('employeeProfileUpdated', employee)
    })

    this.connection.on('EmployeePromoted', (promotion) => {
      console.log('Employee promoted:', promotion)
      this.triggerGlobalEvent('employeePromoted', promotion)
    })

    this.connection.on('EmployeeTransferred', (transfer) => {
      console.log('Employee transferred:', transfer)
      this.triggerGlobalEvent('employeeTransferred', transfer)
    })

    this.connection.on('EmployeeResigned', (resignation) => {
      console.log('Employee resigned:', resignation)
      this.triggerGlobalEvent('employeeResigned', resignation)
    })

    this.connection.on('EmployeeRetired', (retirement) => {
      console.log('Employee retired:', retirement)
      this.triggerGlobalEvent('employeeRetired', retirement)
    })

    // ===== DOCUMENT EVENTS =====
    this.connection.on('DocumentUploaded', (document) => {
      console.log('Document uploaded:', document)
      this.triggerGlobalEvent('documentUploaded', document)
    })

    this.connection.on('DocumentApproved', (document) => {
      console.log('Document approved:', document)
      this.triggerGlobalEvent('documentApproved', document)
    })

    this.connection.on('DocumentRejected', (document) => {
      console.log('Document rejected:', document)
      this.triggerGlobalEvent('documentRejected', document)
    })

    // ===== SYSTEM EVENTS =====
    this.connection.on('SystemMaintenance', (maintenance) => {
      console.log('System maintenance:', maintenance)
      this.triggerGlobalEvent('systemMaintenance', maintenance)
    })

    this.connection.on('DataBackupCompleted', (backup) => {
      console.log('Data backup completed:', backup)
      this.triggerGlobalEvent('dataBackupCompleted', backup)
    })

    this.connection.on('UserSessionExpired', (session) => {
      console.log('User session expired:', session)
      this.triggerGlobalEvent('userSessionExpired', session)
    })

    // ===== APPROVAL WORKFLOW EVENTS =====
    this.connection.on('ApprovalRequestSubmitted', (request) => {
      console.log('Approval request submitted:', request)
      this.triggerGlobalEvent('approvalRequestSubmitted', request)
    })

    this.connection.on('ApprovalRequestAssigned', (request) => {
      console.log('Approval request assigned:', request)
      this.triggerGlobalEvent('approvalRequestAssigned', request)
    })

    this.connection.on('ApprovalRequestApproved', (request) => {
      console.log('Approval request approved:', request)
      this.triggerGlobalEvent('approvalRequestApproved', request)
    })

    this.connection.on('ApprovalRequestRejected', (request) => {
      console.log('Approval request rejected:', request)
      this.triggerGlobalEvent('approvalRequestRejected', request)
    })

    this.connection.on('ApprovalRequestReturned', (request) => {
      console.log('Approval request returned:', request)
      this.triggerGlobalEvent('approvalRequestReturned', request)
    })

    // ===== REPORT EVENTS =====
    this.connection.on('ReportGenerated', (report) => {
      console.log('Report generated:', report)
      this.triggerGlobalEvent('reportGenerated', report)
    })

    this.connection.on('ReportExportCompleted', (report) => {
      console.log('Report export completed:', report)
      this.triggerGlobalEvent('reportExportCompleted', report)
    })

    // ===== AUDIT EVENTS =====
    this.connection.on('AuditLogCreated', (audit) => {
      console.log('Audit log created:', audit)
      this.triggerGlobalEvent('auditLogCreated', audit)
    })

    this.connection.on('SecurityAlert', (alert) => {
      console.log('Security alert:', alert)
      this.triggerGlobalEvent('securityAlert', alert)
    })
  }

  // Handle notification events
  handleNotification(notification) {
    const { t } = useI18n()
    
    // Show toast notification based on priority
    const priority = notification.priority || 2
    const messageType = priority >= 4 ? 'error' : priority >= 3 ? 'warning' : 'info'
    
    ElMessage({
      type: messageType,
      message: notification.title,
      description: notification.message,
      duration: priority >= 4 ? 0 : 5000, // High priority notifications don't auto-dismiss
      showClose: true
    })

    // Play notification sound for high priority notifications
    if (priority >= 3) {
      this.playNotificationSound()
    }

    // Update notification count in store
    this.updateNotificationCount()
  }

  // Play notification sound
  playNotificationSound() {
    try {
      const audio = new Audio('/sounds/notification.mp3')
      audio.volume = 0.5
      audio.play().catch(error => {
        console.warn('Failed to play notification sound:', error)
      })
    } catch (error) {
      console.warn('Notification sound not available:', error)
    }
  }

  // Update notification count
  updateNotificationCount() {
    // This would typically update a Vuex store
    // For now, we'll just log it
    console.log('Notification count should be updated')
  }

  // Disconnect from SignalR
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop()
      } catch (error) {
        console.error('Error disconnecting from SignalR:', error)
      } finally {
        this.connection = null
        this.updateConnectionStatus('disconnected', 'Disconnected')
      }
    }
  }

  // Handle connection errors
  handleConnectionError(error) {
    console.error('SignalR connection error:', error)
    
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.resilience.autoReconnect) {
      this.reconnectAttempts++
      const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay)
      
      this.updateConnectionStatus('reconnecting', `Reconnecting in ${delay/1000}s (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
      
      setTimeout(() => {
        this.connect()
      }, delay)
    } else {
      this.updateConnectionStatus('failed', 'Connection failed after maximum attempts')
      ElMessage.error('Failed to connect to real-time service')
    }
  }

  // Handle disconnection
  handleDisconnection(error) {
    if (this.resilience.autoReconnect && this.resilience.networkOnline) {
      this.handleConnectionError(error)
    }
  }

  // Update connection status
  updateConnectionStatus(status, details) {
    this.connectionStatus = status
    this.connectionStatusDetails = details
    console.log(`SignalR Status: ${status} - ${details}`)
  }

  // Get authentication token
  getAuthToken() {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken')
  }

  // Get current user ID
  getCurrentUserId() {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user.id || user.userId
  }

  // Register event handler for specific event
  on(eventName, handler) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, [])
    }
    this.eventHandlers.get(eventName).push(handler)
  }

  // Register global event handler
  onGlobal(eventName, handler) {
    if (!this.globalEventHandlers.has(eventName)) {
      this.globalEventHandlers.set(eventName, [])
    }
    this.globalEventHandlers.get(eventName).push(handler)
  }

  // Remove event handler
  off(eventName, handler) {
    if (this.eventHandlers.has(eventName)) {
      const handlers = this.eventHandlers.get(eventName)
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  // Remove global event handler
  offGlobal(eventName, handler) {
    if (this.globalEventHandlers.has(eventName)) {
      const handlers = this.globalEventHandlers.get(eventName)
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  // Trigger global event
  triggerGlobalEvent(eventName, data) {
    if (this.globalEventHandlers.has(eventName)) {
      this.globalEventHandlers.get(eventName).forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          console.error(`Error in global event handler for ${eventName}:`, error)
        }
      })
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      status: this.connectionStatus,
      details: this.connectionStatusDetails,
      isConnected: this.connection?.state === 'Connected',
      connectionHealth: this.connectionHealth,
      resilience: this.resilience
    }
  }

  // Get connection health
  getConnectionHealth() {
    return this.connectionHealth
  }

  // Get resilience status
  getResilienceStatus() {
    return this.resilience
  }

  // Set auto reconnect
  setAutoReconnect(enabled) {
    this.resilience.autoReconnect = enabled
  }

  // Force reconnect
  async forceReconnect() {
    await this.disconnect()
    this.reconnectAttempts = 0
    await this.connect()
  }

  // Send message to hub
  async sendMessage(methodName, ...args) {
    if (this.connection?.state === 'Connected') {
      try {
        return await this.connection.invoke(methodName, ...args)
      } catch (error) {
        console.error(`Error sending message ${methodName}:`, error)
        throw error
      }
    } else {
      throw new Error('SignalR connection not available')
    }
  }

  // Join group
  async joinGroup(groupName) {
    return await this.sendMessage('JoinGroup', groupName)
  }

  // Leave group
  async leaveGroup(groupName) {
    return await this.sendMessage('LeaveGroup', groupName)
  }

  // Send notification acknowledgment
  async acknowledgeNotification(notificationId) {
    return await this.sendMessage('AcknowledgeNotification', notificationId)
  }

  // Send user activity
  async sendUserActivity(activity) {
    return await this.sendMessage('UserActivity', {
      ...activity,
      timestamp: new Date().toISOString(),
      userId: this.getCurrentUserId()
    })
  }

  // Send heartbeat
  async sendHeartbeat() {
    return await this.sendMessage('Heartbeat', {
      timestamp: Date.now(),
      userId: this.getCurrentUserId()
    })
  }
}

// Create singleton instance
const signalRService = new SignalRService()

export default signalRService 